import {
  cardinalWindComponents,
  normalizeCardinalDegrees,
  relativeCardinalDegrees,
} from "./wind.js";
import { ENGAGEMENT_ZONE_FIELD_NAMES } from "./engagement-zone.js";

export {
  cardinalWindComponents,
  normalizeCardinalDegrees,
  relativeCardinalDegrees,
} from "./wind.js";

export const TRAJECTORY_MIN_RANGE_INCREMENT_YARDS = 1;
export const TRAJECTORY_MAX_RANGE_INCREMENT_YARDS = 500;
export const TRAJECTORY_MIN_TARGET_RANGE_YARDS = 100;
export const TRAJECTORY_MAX_TARGET_RANGE_YARDS = 3000 / 0.9144;

function finiteNumber(value, label) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    throw new TypeError(`${label} must be a finite number.`);
  }
  return numeric;
}

function requiredNumber(parameters, name, label) {
  const value = parameters.get(name);
  if (value === null || String(value).trim() === "") {
    throw new TypeError(`${label} is required.`);
  }
  return finiteNumber(value, label);
}

function optionalNumber(parameters, name, label) {
  const value = parameters.get(name);
  if (value === null || String(value).trim() === "") return 0;
  return finiteNumber(value, label);
}

/** Use the legacy engine's one-yard calculation grid through the exact target. */
export function solutionRangeIncrement(targetRangeYards) {
  finiteNumber(targetRangeYards, "Target range");
  return TRAJECTORY_MIN_RANGE_INCREMENT_YARDS;
}

/** Return the exact target sample, never a merely nearby early-termination row. */
export function findSolutionTargetSample(
  samples, targetRangeYards, epsilon = 1e-6,
) {
  const target = finiteNumber(targetRangeYards, "Target range");
  const tolerance = finiteNumber(epsilon, "Target range tolerance");
  if (tolerance < 0) {
    throw new RangeError("Target range tolerance must not be negative.");
  }
  const rows = Array.isArray(samples) ? samples : [];
  return [...rows].reverse().find(({ range }) => (
    Number.isFinite(range) && Math.abs(range - target) <= tolerance
  )) || null;
}

function solutionLatitude(parameters) {
  const rawLatitude = parameters.get("lat");
  if (rawLatitude === null || String(rawLatitude).trim() === "") return null;
  const latitude = finiteNumber(rawLatitude, "Latitude");
  if (latitude < -90 || latitude > 90) {
    throw new RangeError("Latitude must be between -90° and 90°.");
  }
  return latitude;
}

/**
 * Translate Solution-only controls into an ordinary trajectory request.
 * The returned URLSearchParams is a clone; the caller's data is never mutated.
 */
export function prepareSolutionParameters(formData) {
  const parameters = new URLSearchParams(formData);
  const targetRangeYards = requiredNumber(
    parameters, "tg", "Target range",
  );
  if (targetRangeYards < TRAJECTORY_MIN_TARGET_RANGE_YARDS
    || targetRangeYards > TRAJECTORY_MAX_TARGET_RANGE_YARDS) {
    throw new RangeError(
      `Target range must be between ${TRAJECTORY_MIN_TARGET_RANGE_YARDS} and ${TRAJECTORY_MAX_TARGET_RANGE_YARDS} yards.`,
    );
  }

  const targetBearingDegrees = normalizeCardinalDegrees(optionalNumber(
    parameters, "tb", "Target bearing",
  ));
  const windSpeedMph = optionalNumber(parameters, "ws", "Wind speed");
  if (windSpeedMph < 0 || windSpeedMph > 100) {
    throw new RangeError("Wind speed must be between 0 and 100 mph.");
  }
  const windFromDegrees = normalizeCardinalDegrees(optionalNumber(
    parameters, "wd", "Wind direction",
  ));
  const relativeWindFromDegrees = relativeCardinalDegrees(
    windFromDegrees, targetBearingDegrees,
  );
  const { wx, wz } = cardinalWindComponents(
    windSpeedMph, windFromDegrees, targetBearingDegrees,
  );
  const latitudeDegrees = solutionLatitude(parameters);
  const rangeIncrementYards = solutionRangeIncrement(targetRangeYards);

  // These four controls describe the shot in cardinal terms and are not part
  // of the trajectory CGI API. All other inputs, including optional vertical
  // wind and lateral-lead speed/direction, pass through unchanged.
  for (const name of [
    "tg", "tb", "ws", "wd", ...ENGAGEMENT_ZONE_FIELD_NAMES,
  ]) parameters.delete(name);
  parameters.set("wx", String(wx));
  parameters.set("wz", String(wz));
  parameters.set("faz", String(targetBearingDegrees));
  parameters.set("rn", "0");
  parameters.set("rx", String(targetRangeYards));
  parameters.set("ri", String(rangeIncrementYards));
  parameters.set("elc", "on");
  parameters.set("azc", "on");

  if (latitudeDegrees === null) {
    parameters.delete("lat");
    parameters.delete("coc");
  } else {
    parameters.set("lat", String(latitudeDegrees));
    parameters.set("coc", "on");
  }

  return {
    parameters,
    metadata: {
      targetRangeYards,
      targetBearingDegrees,
      windSpeedMph,
      windFromDegrees,
      relativeWindFromDegrees,
      rangeWindMph: wx,
      crosswindMph: wz,
      latitudeDegrees,
      coriolisEnabled: latitudeDegrees !== null,
      rangeIncrementYards,
    },
  };
}

/** Run the trajectory engine once and retain its complete parsed response. */
export async function calculateSolution(formData, runTrajectory) {
  if (typeof runTrajectory !== "function") {
    throw new TypeError("runTrajectory must be a function.");
  }
  const { parameters, metadata } = prepareSolutionParameters(formData);
  const trajectoryResult = await runTrajectory(parameters, "Target solution");
  return {
    ...trajectoryResult,
    kind: "solution",
    metadata: {
      ...(trajectoryResult?.metadata || {}),
      ...metadata,
    },
  };
}
